import { gql, type TypedDocumentNode } from '@apollo/client';

import type {
  CompanyDashboard,
  CompanyInvitationList,
  CompanyInvitationPreview,
  CompanyInvitationStatus,
  CompanyMembership,
  CompanyProfile,
  CompanySettings,
  MyCompanyInvitation,
} from '../types/companyPortal.types';
import { COMPANY_INVITATION_FIELDS, COMPANY_PROFILE_FIELDS } from './fragments';

/**
 * Every company the signed-in account belongs to — the workspace gate (Company module: portal
 * access is derived from MEMBERSHIP, never from a platform role). Authenticated-only; always
 * scoped server-side to the current user.
 */
export const MY_COMPANY_MEMBERSHIPS_QUERY: TypedDocumentNode<{
  myCompanyMemberships: CompanyMembership[];
}> = gql`
  query MyCompanyMemberships {
    myCompanyMemberships {
      companyId
      companyName
      companyCode
      companyLogoUrl
      role
      status
      joinedAt
      companyStatus
    }
  }
`;

/** Headline counts for the Company Dashboard (Jobs/Applications/Interviews/Offers are backend
 * placeholders — literal zeros until those modules ship; rendered as-is, never fabricated). */
export const COMPANY_DASHBOARD_QUERY: TypedDocumentNode<
  { companyDashboard: CompanyDashboard | null },
  { companyId: string }
> = gql`
  query CompanyDashboard($companyId: UUID!) {
    companyDashboard(companyId: $companyId) {
      branches
      departments
      teams
      members
      pendingInvitations
      jobs
      applications
      interviews
      offers
    }
  }
`;

/** The full company profile for the portal Profile page. */
export const COMPANY_PROFILE_QUERY: TypedDocumentNode<
  { company: CompanyProfile | null },
  { id: string }
> = gql`
  query CompanyProfileById($id: UUID!) {
    company(id: $id) {
      ...CompanyProfileFields
    }
  }
  ${COMPANY_PROFILE_FIELDS}
`;

/** The Company User Policy (Settings page). */
export const COMPANY_SETTINGS_QUERY: TypedDocumentNode<
  { companySettings: CompanySettings | null },
  { companyId: string }
> = gql`
  query CompanySettings($companyId: UUID!) {
    companySettings(companyId: $companyId) {
      companyId
      corporateEmailOnly
      allowExternalUsers
      invitationExpiryDays
      allowedDomains
    }
  }
`;

/** Offset page of invitations for the management table — status comes back effective (lazy expiry). */
export const COMPANY_INVITATIONS_QUERY: TypedDocumentNode<
  { companyInvitations: CompanyInvitationList | null },
  {
    companyId: string;
    page?: number;
    pageSize?: number;
    search?: string | null;
    statuses?: CompanyInvitationStatus[] | null;
    sortBy?: string | null;
    sortDescending?: boolean;
  }
> = gql`
  query CompanyInvitations(
    $companyId: UUID!
    $page: Int
    $pageSize: Int
    $search: String
    $statuses: [CompanyInvitationStatus!]
    $sortBy: String
    $sortDescending: Boolean
  ) {
    companyInvitations(
      companyId: $companyId
      page: $page
      pageSize: $pageSize
      search: $search
      statuses: $statuses
      sortBy: $sortBy
      sortDescending: $sortDescending
    ) {
      items {
        ...CompanyInvitationFields
      }
      totalCount
      page
      pageSize
      totalPages
      hasNextPage
      hasPreviousPage
    }
  }
  ${COMPANY_INVITATION_FIELDS}
`;

/**
 * The pending invitations addressed to the SIGNED-IN user's own verified email (Invitation
 * Discovery). Authenticated-only and always scoped server-side to the caller's identity — no email
 * argument is accepted, so it can never be used to probe another address. Drives the onboarding
 * "Pending invitations" card and the /account/invitations surface.
 */
export const MY_COMPANY_INVITATIONS_QUERY: TypedDocumentNode<{
  myCompanyInvitations: MyCompanyInvitation[];
}> = gql`
  query MyCompanyInvitations {
    myCompanyInvitations {
      invitationId
      companyId
      companyName
      companyLogoUrl
      role
      invitedAt
      expiresAt
      status
    }
  }
`;

/**
 * Anonymous invitation resolution for the landing page. Any unresolvable token — tampered,
 * cancelled, unknown — comes back `null` with no distinction (anti-enumeration); the page shows
 * one generic message and never hints whether a token ever existed.
 */
export const COMPANY_INVITATION_BY_TOKEN_QUERY: TypedDocumentNode<
  { companyInvitationByToken: CompanyInvitationPreview | null },
  { token: string }
> = gql`
  query CompanyInvitationByToken($token: String!) {
    companyInvitationByToken(token: $token) {
      companyId
      companyName
      companyLogoUrl
      email
      role
      status
      expiresAt
      accountExists
    }
  }
`;
